<template>
  <div
    id="register"
    v-if="event.id">
    <!-- Event Banner -->
    <div class="container-fluid">
      <div class="row border-bottom border-muted py-3">
        <div class="col">
          <h1 class="text-center text-lg-left mb-4">{{event.name}}</h1>

          <!-- Event Details -->
          <div class="d-flex flex-column flex-lg-row">
            <h6 class="event-details mb-0">
              <!-- Registration Open -->
              <div class="d-flex mb-1">
                <div class="label text-highlight mr-3 text-right text-lg- ml-auto ml-lg-0">
                  Registration Open:
                </div>
                <div class="mr-auto">
                  {{event.display_registration_start_date_time}}
                </div>
              </div>
              <!-- / Registration Open -->

              <!-- Registration Closed -->
              <div class="d-flex mb-1">
                <div class="label text-highlight mr-3 text-right ml-auto ml-lg-0">
                  Registration Closed:
                </div>
                <div class="mr-auto">
                  {{event.display_registration_end_date_time}}
                </div>
              </div>
              <!-- / Registration Closed -->
            </h6>

            <h6 class="event-details">
              <!-- Event Start -->
              <div class="d-flex mb-1">
                <div class="label text-highlight mr-3 text-right ml-auto ml-lg-3">
                  Event Start:
                </div>
                <div class="mr-auto">
                  {{event.display_start_date_time}}
                </div>
              </div>
              <!-- / Event Start -->

              <!-- Event End -->
              <div class="d-flex">
                <div class="label text-highlight mr-3 text-right ml-auto ml-lg-3">
                  Event End:
                </div>
                <div class="mr-auto">
                  {{event.display_end_date_time}}
                </div>
              </div>
              <!-- / Event End -->
            </h6>
          </div>
          <!-- / Event Details -->

          <h5
            v-if="!event.registration_open && event.waitlist_open"
            class="text-danger mt-3">
            Only the waitlist is open at this time.
          </h5>

          <h5
            v-else-if="!event.registration_open && !event.waitlist_open"
            class="text-danger mt-3">
            Registration (including waitlist) is not open at this time.
          </h5>
        </div>
      </div>
    </div>
    <!-- / Event Banner -->

    <!-- Registrant Form (shown after pricing selection) -->
    <div
      v-if="selectedPrice.id"
      id="registrant-form-section"
      class="container-fluid">
      <div class="row mt-3">
        <div class="col">
          <h3 class="mb-3">
            Who will be attending: {{event.name}}?
          </h3>
          <h6 class="mb-4 text-highlight">
            Pricing: {{selectedPrice.label}}
          </h6>

          <b-form
            id="registrant-form"
            name="registrantForm"
            @submit.prevent="addToCart">
            <!-- Basic Info: First Name / Middle Name / Last Name -->
            <div class="row">
              <div class="col-lg">
                <label for="basic-info-first-name">
                  Registrant First Name
                  <small class="text-danger">required</small>
                </label>
                <b-form-input
                  id="basic-info-first-name"
                  ref="basicInfoFirstName"
                  name="basicInfoFirstName"
                  type="text"
                  placeholder="Enter a first name"
                  :disabled="!!basicInfoLoadingMsg"
                  v-model="basicInfo.first_name">
                </b-form-input>
              </div>

              <div class="col-lg mt-3 mt-lg-0">
                <label for="basic-info-middle-name">Registrant Middle Name</label>
                <b-form-input
                  id="basic-info-middle-name"
                  name="basicInfoMiddleName"
                  type="text"
                  placeholder="Enter a middle name"
                  :disabled="!!basicInfoLoadingMsg"
                  v-model="basicInfo.middle_name">
                </b-form-input>
              </div>

              <div class="col-lg mt-3 mt-lg-0">
                <label for="basic-info-last-name">
                  Registrant Last Name
                  <small class="text-danger">required</small>
                </label>
                <b-form-input
                  id="basic-info-last-name"
                  name="basicInfoLastName"
                  type="text"
                  placeholder="Enter a last name"
                  :disabled="!!basicInfoLoadingMsg"
                  v-model="basicInfo.last_name">
                </b-form-input>
              </div>
            </div>

            <!-- Basic Info: DOB / Email / Phone -->
            <div class="row mt-3">
              <div class="col-lg">
                <label for="basic-info-dob">
                  Date Of Birth
                  <small class="text-danger">required</small>
                </label>
                <DatePicker
                  id="basic-info-dob"
                  name="basicInfoDob"
                  :disabled="!!basicInfoLoadingMsg"
                  v-model="basicInfo.birthdate"
                />
              </div>

              <div class="col-lg mt-3 mt-lg-0">
                <label for="basic-info-email">
                  Email
                  <small class="text-danger">required</small>
                </label>
                <b-form-input
                  id="basic-info-email"
                  name="basicInfoEmail"
                  type="email"
                  placeholder="Enter an email address"
                  :disabled="!!basicInfoLoadingMsg"
                  v-model="basicInfo.email">
                </b-form-input>
              </div>

              <div class="col-lg mt-3 mt-lg-0">
                <label for="basic-info-phone">
                  Phone
                  <small class="text-danger">required</small>
                </label>
                <b-form-input
                  id="basic-info-phone"
                  name="basicInfoPhone"
                  type="tel"
                  placeholder="Enter a phone number"
                  :disabled="!!basicInfoLoadingMsg"
                  v-model="basicInfo.primary_phone">
                </b-form-input>
              </div>
            </div>

            <div class="row mb-3">
              <small class="d-block mt-1 mb-n4 col-sm col-12">
                <br />
                By providing your email and phone number during registration you agree to receive email and/or text communications from this organization regarding event related information.
                <br /><br />
              </small>
            </div>

            <!-- Additional Form Fields (if any) -->
            <div
              v-if="eventFormFields.length > 0"
              class="mt-4">
              <hr>
              <FormFields
                :form-fields="orderedFormFields"
                v-model="formData"
                hide-buttons
              />
            </div>

            <!-- Form Buttons -->
            <div class="row justify-content-end mt-4">
              <div class="col-lg-auto pr-lg-0">
                <b-button
                  type="submit"
                  class="w-100 w-lg-auto"
                  variant="success"
                  :disabled="!!basicInfoLoadingMsg">
                  Add To Cart
                </b-button>
              </div>

              <div class="col-lg-auto mt-lg-0 mt-3">
                <b-button
                  type="button"
                  class="w-100 w-lg-auto"
                  variant="danger"
                  :disabled="!!basicInfoLoadingMsg"
                  @click="cancelRegistrantForm">
                  Cancel
                </b-button>
              </div>
            </div>
            <!-- / Form Buttons -->
          </b-form>

          <!-- Loading Indicator -->
          <Loader
            id="basic-info-loading"
            variant="light"
            :msg="basicInfoLoadingMsg">
          </Loader>
          <!-- / Loading Indicator -->
        </div>
      </div>
    </div>
    <!-- / Registrant Form -->

    <!-- Event Pricing / Details -->
    <div
      v-else
      class="container mx-0">
      <div class="row mt-3">
        <!-- Pricing -->
        <div id="price-list" class="col-lg">
          <h5 class="text-highlight mb-3">Choose Your Pricing</h5>

          <div
            v-if="eventStatus(event).reason == 'Registration Closed (No Pricing)'">
            There is no pricing available for this event.
          </div>

          <b-button
            class="w-100 mb-3 d-flex align-items-center"
            type="button"
            variant="primary"
            v-for="price in sortedPricingPackages"
            :key="price.id"
            @click="beginRegistration(price)">
            <!-- Label & Ages -->
            <div class="flex-fill text-left">
              <h6 class="mb-0">
                {{price.label}}
              </h6>

              <div>
                <span v-if="price.min_age && price.max_age">
                  Ages: {{price.min_age}} - {{price.max_age}}
                </span>

                <span v-else-if="price.min_age || price.max_age">
                  Age: {{price.min_age || price.max_age}}
                </span>
              </div>
            </div>
            <!-- / Label & Ages -->

            <!-- Price -->
            <div
              class="text-right"
              style="white-space: nowrap;">
              <h6
                class="mb-0"
                v-if="!event.registration_open && event.waitlist_open">
                No charge due for waitlist<br>
                <small>
                  {{price.price | currency}}
                  <span v-if="!price.price_fees_included">+ fees</span>
                  due upon arrival
                </small>
              </h6>

              <h6
                class="mb-0"
                v-else>
                {{price.price | currency}}
                <span v-if="!price.price_fees_included">+ fees</span>
              </h6>
            </div>
            <!-- / Price  -->
          </b-button>
        </div>
        <!-- / Pricing -->

        <div class="w-100 mb-3 border-bottom border-muted d-lg-none"></div>

        <!-- Event Details -->
        <div class="col-lg">
          <h5 class="text-highlight">{{event.long_name}}</h5>
          <p v-html="newlineToBr(event.description)"></p>

          <h6 class="text-highlight">Date & Time</h6>
          <p>{{event.display_start_date_time}} until {{event.display_end_date_time}}</p>

          <h6 class="text-highlight">Location</h6>
          <p v-html="newlineToBr(event.location)"></p>

          <h6 class="text-highlight">Cost</h6>
          <p>
            <span
              v-for="price in event.pricing_packages"
              :key="price.id">
              &bull; {{price.label}} - ${{price.price}}<br>
            </span>
          </p>

          <h6 class="text-highlight">Refund Policy</h6>
          <p v-html="newlineToBr(event.refund_policy)"></p>

          <h6 class="text-highlight">Contact</h6>
          <p v-html="newlineToBr(event.contact_text)"></p>

          <p class="text-highlight">
            <i v-html="newlineToBr(event.compliance_statement)"></i>
          </p>
        </div>
        <!-- / Event Details -->
      </div>
    </div>
    <!-- / Event Pricing / Details -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import AppMixin from '../services/mixin.app';
import DirtyForm from '../services/dirty-form';
import LocalStorageMixin from '../services/mixin.local-storage';

export default {
  name: 'register',
  mixins: [
    AppMixin,
    DirtyForm,
    LocalStorageMixin,
  ],
  data() {
    return {
      selectedPrice: {},
      basicInfo: {},
      basicInfoLoadingMsg: '',
      eventFormFields: [],
      formData: {},
    };
  },
  computed: {
    isBasicInfoDirty() {
      return this.basicInfo.first_name
        || this.basicInfo.middle_name
        || this.basicInfo.last_name
        || this.basicInfo.birthdate
        || this.basicInfo.email
        || this.basicInfo.primary_phone
        || (this.formData && Object.keys(this.formData).length > 0);
    },
    routeChangeDirty() {
      return this.isBasicInfoDirty;
    },
    sortedPricingPackages() {
      return _(this.event.pricing_packages)
        .filter({ is_open: true })
        .orderBy('label')
        .value();
    },
    orderedFormFields() {
      return _.orderBy(this.eventFormFields, 'order');
    },
  },
  methods: {
    beginRegistration(price) {
      this.selectedPrice = _.clone(price);

      // if event registration not open
      if (!this.eventStatus(this.event).canRegister) {
        this.selectedPrice = {};
        return this.showWarning(
          'Registration is closed for this event.',
          'You cannot register for this event.',
        );
      }

      const showForm = () => this.loadEventFormFields();

      // if user logged in, load form fields
      if (this.loggedInUser.id) {
        return showForm();
      }

      // if no one logged in, force sign in
      return this.showSignIn().then(showForm).catch(() => {
        this.resetForm();
      });
    },
    loadEventFormFields() {
      this.basicInfoLoadingMsg = 'Loading registration form...';

      let sessionPromise = Promise.resolve();
      if (!this.registrationSession.uuid) {
        sessionPromise = DataSvc.startRegistrationSession()
          .then((s) => {
            this.registrationSession = s;
          });
      }

      return sessionPromise
        .then(() => DataSvc.getEventForms(
          this.registrationSession.uuid,
          this.event.id,
        ))
        .then((forms) => {
          const form = _.first(forms);
          this.eventFormFields = form
            ? _.reject(form.form_fields, { hidden: true })
            : [];
        })
        .finally(() => {
          this.basicInfoLoadingMsg = '';
        });
    },
    getMissingFormFields() {
      const requiredFields = _.filter(this.eventFormFields, 'required');

      return _.filter(requiredFields, (field) => {
        let enteredValue = (this.formData || {})[field.name];

        if (typeof enteredValue === 'string') {
          enteredValue = enteredValue.trim();
        }

        return !enteredValue;
      });
    },
    addToCart() {
      const missingFormFields = this.getMissingFormFields();

      if (missingFormFields.length > 0) {
        const missingList = _.map(missingFormFields, f => `&bull; ${_.escape(f.label)}`).join('<br>');
        return this.showWarning(
          `The following required fields are missing:<br><br>${missingList}`,
          'Invalid Registration',
        );
      }

      const registrant = _.cloneDeep(this.basicInfo);
      registrant.pricing_package_id = this.selectedPrice.id;
      registrant.roster_id = this.event.rosters[0].id;
      registrant.form_data = _.cloneDeep(this.formData);

      this.basicInfoLoadingMsg = true;

      let registrationPromise = Promise.resolve();
      if (!this.registrationSession.uuid) {
        registrationPromise = DataSvc.startRegistrationSession()
          .then((s) => {
            this.registrationSession = s;
          });
      }

      return registrationPromise
        .then(() => DataSvc.addRegistration(this.registrationSession.uuid, registrant))
        .then(this.updateActiveRegistrationSession)
        .then(() => {
          this.resetForm();
          this.goToCart();
        })
        .finally(() => {
          this.basicInfoLoadingMsg = false;
        });
    },
    cancelRegistrantForm() {
      if (this.isBasicInfoDirty) {
        return this.showConfirm('You have unsaved data. Do you want to abandon these changes?')
          .then(this.resetForm, () => {});
      }
      this.resetForm();
      return Promise.resolve();
    },
    resetForm() {
      this.basicInfo = {};
      this.formData = {};
      this.eventFormFields = [];
      this.selectedPrice = {};
    },
  },
  created() {
    const initRegister = () => {
      const { eventID, hiddenCode } = this.$route.params;
      let eventPromise = Promise.resolve();

      if (parseInt(eventID) !== parseInt(this.event.id)) {
        this.showGlobalLoader('Loading event...');
        this.event.id = null;
        eventPromise = DataSvc.getEvent(eventID, hiddenCode)
          .then((event) => {
            this.event = event;
            this.localStorageSet('eventID', event.id);
          })
          .finally(this.hideGlobalLoader);
      }

      return eventPromise;
    };

    this.initPromise.then(initRegister);
  },
};
</script>

<style lang="scss">
#register {
  .price {
    border-bottom: 1px solid var(--muted);

    * {
      cursor: pointer;
    }

    &:last-child {
      border-bottom: none;
    }

    .custom-control-label {
      width: 100%;
    }
  }

  #registrant-form-section {
    label {
      color: var(--highlight);
    }
  }

  @media (min-width: 992px) {
    .event-details:nth-child(2) {
      .label {
        min-width: 85px;
      }
    }
    #price-list {
      max-width: 500px;
    }
  }
}
</style>
